import React, { useState, useEffect } from "react";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  EyeIcon,
  ArrowPathIcon,
  FunnelIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import useReferrallist from "../stores/referrals";

export default function Referrals() {
  // const [referrals, setReferrals] = useState(initialReferrals);
  // const [topReferrers, setTopReferrers] = useState(initialTopReferrers);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 43,
    activeReferrals: 32,
    conversionRate: 74.4,
    totalCommission: 966.5,
  });
  const [loading, setLoading] = useState(false);
  const {
    dashboard,
    list,
    loaded,
    actions: { getDashboard, getList },
  } = useReferrallist();

  useEffect(() => {
    if (!loaded) {
      getDashboard();
      getList({});
    }
  }, [loaded]);

  useEffect(() => {
    let debounceTimer = setTimeout(() => {
      getList({
        search: searchTerm,
        active: statusFilter,
        start_date: dateFilter.startDate,
        end_date: dateFilter.endDate,
        page,
        size: limit,
      });
    }, 1500);
    return () => clearTimeout(debounceTimer);
  }, [
    searchTerm,
    statusFilter,
    dateFilter.startDate,
    dateFilter.endDate,
    page,
    limit,
  ]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return amount ? `$${amount.toFixed(2)}` : "$0.00";
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setDateFilter({ startDate: "", endDate: "" });
  };

  // Export data to CSV
  const exportToCSV = () => {
    const csvContent = ["data:text/csv;charset=utf-8,"];

    // Add headers
    csvContent.push(
      "Referrer UID,Referrer Name,Referred UID,Referred Name,Date,Status,Conversion Date\n",
    );

    // Add data rows
    list.forEach((referral) => {
      csvContent.push(
        `${referral.user.id},${referral.user.first_name} ${
          referral.user.last_name
        },${referral.referred_user.id},${referral.referred_user.first_name} ${
          referral.referred_user.last_name
        },${referral.created_at},${referral.active},${
          referral.created_at || "N/A"
        }\n`,
      );
    });

    // Create download link
    const encodedUri = encodeURI(csvContent.join(""));
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `referrals-export-${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Referrals Management
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage all referrals across the platform
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
          <div className="flex items-center">
            <UserGroupIcon className="h-10 w-10 text-blue-500 mr-3" />
            <div>
              <h3 className="text-sm text-gray-500">Total Referrals</h3>
              <p className="text-2xl ">{dashboard.total_referral}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ChartBarIcon className="h-10 w-10 text-green-500 mr-3" />
            <div>
              <h3 className="text-sm text-gray-500">Active Referrals</h3>
              <p className="text-2xl ">{dashboard.active_referrer}</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-10 w-10 text-yellow-500 mr-3" />
            <div>
              <h3 className="text-sm text-gray-500">Total Commission</h3>
              <p className="text-2xl ">{formatCurrency(dashboard.total_commission)}</p>
            </div>
          </div>
        </div> */}

        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ChartBarIcon className="h-10 w-10 text-purple-500 mr-3" />
            <div>
              <h3 className="text-sm text-gray-500">Conversion Rate</h3>
              <p className="text-2xl ">{dashboard.conversion_rate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Referrers */}
      <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 mb-6">
        <h2 className="text-xl text-gray-800 mb-4">Top Referrers</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="py-2 px-4 border-b text-sm">User UID</th>
                <th className="py-2 px-4 border-b text-sm">Name</th>
                <th className="py-2 px-4 border-b text-sm">Total Referrals</th>
                <th className="py-2 px-4 border-b text-sm">Active Referrals</th>
                {/* <th className="py-2 px-4 border-b text-sm">Total Commission</th> */}
              </tr>
            </thead>
            <tbody>
              {dashboard.top_referrer.map((referrer, rid) => (
                <tr key={rid} className="hover:bg-gray-50">
                  <td className="py-2 px-4 border-b text-left text-sm">
                    {referrer.id}
                  </td>
                  <td className="py-2 px-4 border-b text-left text-sm">
                    {referrer.name}
                  </td>
                  <td className="py-2 px-4 border-b text-left text-sm">
                    {referrer.total_referrals}
                  </td>
                  <td className="py-2 px-4 border-b text-left text-sm">
                    {referrer.active_referrals}
                  </td>
                  {/* <td className="py-2 px-4 border-b">{formatCurrency(referrer.total_commission)}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col lg:flex-row justify-between gap-4 mb-4">
          {/* Search */}
          <div className="flex-grow">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search by UID or name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Expired">Expired</option>
              </select>
            </div>

            <div className="flex gap-2">
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) =>
                  setDateFilter((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) =>
                  setDateFilter((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="End Date"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div>
            <button
              onClick={resetFilters}
              type="button"
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Reset Filters
            </button>
          </div>

          <div>
            <button
              onClick={exportToCSV}
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              Export Data
            </button>
          </div>
        </div>
      </div>

      {/* Referral List */}
      <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
        <h2 className="text-xl text-gray-800 mb-4">Referral List</h2>

        {!loaded ? (
          <div className="flex justify-center items-center py-10">
            <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No referrals found matching your criteria.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="py-2 px-4 border-b text-sm">Referrer UID</th>
                  <th className="py-2 px-4 border-b text-sm">Referrer Name</th>
                  <th className="py-2 px-4 border-b text-sm">Referred UID</th>
                  <th className="py-2 px-4 border-b text-sm">Referred Name</th>
                  <th className="py-2 px-4 border-b text-sm">Referral Date</th>
                  <th className="py-2 px-4 border-b text-sm">Status</th>
                  {/* <th className="py-2 px-4 border-b text-sm">Commission</th> */}
                  <th className="py-2 px-4 border-b text-sm">
                    Conversion Date
                  </th>
                  {/* <th className="py-2 px-4 border-b text-sm">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {list.map((referral, rid) => (
                  <tr key={rid} className="hover:bg-gray-50">
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {referral.referred_user.id}
                    </td>
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {referral.referred_user.first_name}{" "}
                      {referral.referred_user.last_name}
                    </td>
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {referral.user.id}
                    </td>
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {referral.user.first_name} {referral.user.last_name}
                    </td>
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {formatDate(referral.created_at)}
                    </td>
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {/* <span className={`px-2 py-1 rounded-full text-xs ${referral.active ? 'bg-green-100 text-green-800' : referral.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}> */}
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          referral.active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {referral.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {/* <td className="py-2 px-4 border-b text-left text-sm">{formatCurrency(0)}</td> */}
                    <td className="py-2 px-4 border-b text-left text-sm">
                      {formatDate(referral.created_at)}
                    </td>
                    {/* <td className="py-2 px-4 border-b text-left text-sm">
                      <button
                        type="button"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md inline-flex items-center"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
